import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, History, Search, Calendar, User, Tag, Printer, RefreshCw, ChevronLeft, ChevronRight, Download, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const PrintJobHistory = () => {
  const navigate = useNavigate();
  
  // Filter states
  const [barcodeFilter, setBarcodeFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Data states
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Detail dialog
  const [selectedJob, setSelectedJob] = useState(null);

  const fetchJobs = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      
      if (barcodeFilter) params.append('barcode', barcodeFilter);
      if (userFilter) params.append('user_email', userFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const response = await apiClient.get(`/main-labels/print-jobs?${params}`);
      setJobs(response.data.jobs || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error('Error fetching print jobs:', error);
      toast.error('Failed to load print job history');
    } finally {
      setIsLoading(false);
    }
  }, [page, barcodeFilter, userFilter, dateFrom, dateTo]);

  const fetchStats = async () => {
    setIsStatsLoading(true);
    try {
      const response = await apiClient.get('/main-labels/print-jobs/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setIsStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    fetchStats();
  }, []);

  const handleSearch = () => {
    setPage(1);
    fetchJobs();
  };

  const handleClearFilters = () => {
    setBarcodeFilter('');
    setUserFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateStr;
    }
  };

  const getTemplateLabel = (templateType) => {
    const labels = {
      'single_dhoti': 'Single (Dhoti)',
      'single_belt': 'Single (Belt)',
      'combo_2in1': '2-in-1 Combo',
      'combo_3in1': '3-in-1 Combo'
    };
    return labels[templateType] || templateType;
  };

  const exportToCsv = () => {
    if (jobs.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Barcode', 'Pricing Type', 'Template', 'Labels', 'Printed By', 'Date'];
    const csvData = jobs.map(job => [
      job.item_barcode,
      job.pricing_type,
      getTemplateLabel(job.template_type),
      job.label_count,
      job.assigned_by,
      formatDate(job.first_assigned_at)
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `print_job_history_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    toast.success('Export successful');
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/sections/goods-transfer')} 
              className="text-[#F9FAFB] hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Print Job History</h1>
              <p className="text-sm text-[#9CA3AF]">View and track label printing records</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={exportToCsv} 
              variant="outline" 
              size="sm"
              className="text-white border-white/30 hover:bg-white/10"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-white border-none shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Printer className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1A1F2E]">
                    {isStatsLoading ? '...' : stats?.total_labels || 0}
                  </p>
                  <p className="text-xs text-[#5A5F6E]">Total Labels Printed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-none shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1A1F2E]">
                    {isStatsLoading ? '...' : stats?.labels_today || 0}
                  </p>
                  <p className="text-xs text-[#5A5F6E]">Printed Today</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-none shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Tag className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1A1F2E]">
                    {isStatsLoading ? '...' : stats?.unique_barcodes || 0}
                  </p>
                  <p className="text-xs text-[#5A5F6E]">Unique Products</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-none shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <User className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1A1F2E]">
                    {isStatsLoading ? '...' : stats?.top_printers?.length || 0}
                  </p>
                  <p className="text-xs text-[#5A5F6E]">Active Printers</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-white border-none shadow-md">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Search className="w-5 h-5 text-gray-500" />
              Search & Filter
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Barcode</label>
                <Input
                  placeholder="Search barcode..."
                  value={barcodeFilter}
                  onChange={(e) => setBarcodeFilter(e.target.value)}
                  className="h-10"
                  data-testid="barcode-filter"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1 block">User Email</label>
                <Input
                  placeholder="Search user..."
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  className="h-10"
                  data-testid="user-filter"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1 block">From Date</label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="h-10"
                  data-testid="date-from-filter"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1 block">To Date</label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="h-10"
                  data-testid="date-to-filter"
                />
              </div>
              <div className="flex items-end gap-2">
                <Button onClick={handleSearch} className="bg-[#2D5F4F] hover:bg-[#234A3D] h-10 flex-1">
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </Button>
                <Button onClick={handleClearFilters} variant="outline" className="h-10">
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results Table */}
        <Card className="bg-white border-none shadow-md">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <History className="w-5 h-5 text-gray-500" />
                Print Jobs ({total})
              </CardTitle>
              <Button 
                onClick={fetchJobs} 
                variant="ghost" 
                size="sm"
                disabled={isLoading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Barcode</TableHead>
                    <TableHead className="font-semibold">Pricing Type</TableHead>
                    <TableHead className="font-semibold">Template</TableHead>
                    <TableHead className="font-semibold text-center">Labels</TableHead>
                    <TableHead className="font-semibold">Printed By</TableHead>
                    <TableHead className="font-semibold">Date/Time</TableHead>
                    <TableHead className="font-semibold text-center">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                        <p className="text-sm text-gray-500 mt-2">Loading...</p>
                      </TableCell>
                    </TableRow>
                  ) : jobs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <Printer className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                        <p className="text-gray-500">No print jobs found</p>
                        <p className="text-sm text-gray-400">Print some labels to see history here</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    jobs.map((job, idx) => (
                      <TableRow key={idx} className="hover:bg-gray-50">
                        <TableCell className="font-mono font-medium">{job.item_barcode}</TableCell>
                        <TableCell>
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                            {job.pricing_type}
                          </span>
                        </TableCell>
                        <TableCell>{getTemplateLabel(job.template_type)}</TableCell>
                        <TableCell className="text-center">
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-green-100 text-green-700 rounded-full font-bold">
                            {job.label_count}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">{job.assigned_by}</TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {formatDate(job.first_assigned_at)}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedJob(job)}
                            className="h-8"
                            data-testid={`view-job-${idx}`}
                          >
                            <QrCode className="w-4 h-4 mr-1" />
                            View USNs
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-gray-600">
                  Page {page} of {totalPages} ({total} total records)
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1 || isLoading}
                    variant="outline"
                    size="sm"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  <Button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages || isLoading}
                    variant="outline"
                    size="sm"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* USN Detail Dialog */}
      <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5" />
              USN Codes for {selectedJob?.item_barcode}
            </DialogTitle>
          </DialogHeader>
          
          {selectedJob && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Pricing Type:</span>
                  <span className="ml-2 font-medium">{selectedJob.pricing_type}</span>
                </div>
                <div>
                  <span className="text-gray-500">Template:</span>
                  <span className="ml-2 font-medium">{getTemplateLabel(selectedJob.template_type)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Labels Printed:</span>
                  <span className="ml-2 font-medium">{selectedJob.label_count}</span>
                </div>
                <div>
                  <span className="text-gray-500">Printed By:</span>
                  <span className="ml-2 font-medium">{selectedJob.assigned_by}</span>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-2">USN Codes ({selectedJob.usn_codes?.length || 0}):</p>
                <div className="max-h-60 overflow-y-auto bg-gray-50 rounded-lg p-3">
                  <div className="flex flex-wrap gap-2">
                    {selectedJob.usn_codes?.map((usn, idx) => (
                      <span 
                        key={idx}
                        className="px-2 py-1 bg-white border border-gray-200 rounded text-xs font-mono"
                      >
                        {usn}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              
              {selectedJob.optional_barcode && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <span className="text-sm text-blue-700">
                    <strong>Optional Barcode (QR2):</strong> {selectedJob.optional_barcode}
                  </span>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrintJobHistory;
